import { ReactNode, useCallback, useMemo } from "react";
import {
  GridEventListener, GridFilterModel, GridSortModel, GridValueOptionsParams, GridColumnVisibilityModel,
  GridRenderCellParams,
  getGridDateOperators, getGridSingleSelectOperators, getGridStringOperators
} from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import { ContactDenorm, ContactOrgDenorm, AbstractEventAnalyticsLabel } from "../../../store";
import { Company, Tag } from "../../../store/cabinetApi/generated/crm";
import CRMTable, { CRMGridColDef } from '../CRMTable';
import { Box, SxProps } from "@mui/material";
import colors from "../../../colors";
import { selectFilterOperator } from "../../DataGrid/utils";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import { CabButton } from "@CabComponents/CabButton";


const contactColumns: CRMGridColDef<ContactDenorm>[] = [
  {
    field: 'name',
    headerName: 'Name',
    type: 'string',
    display: "flex",
    flex: 2,
    minWidth: 250,
    filterOperators: getGridStringOperators().filter(op => !['isAnyOf', 'isEmpty', 'isNotEmpty'].includes(op.value)),
    valueGetter: (value, row, column, apiRef) => value || '',
  },
  {
    field: 'primary_email',
    headerName: 'Email',
    display: "flex",
    type: 'string',
    width: 200,
  },
  {
    field: 'company',
    headerName: 'Company',
    type: 'singleSelect',
    display: "flex",
    sortable: false,
    width: 200,
    renderCell: (params) => {
      return params.row.company?.name ?? "";
    },
    filterOperators: getGridSingleSelectOperators().filter(op => op.value !== 'not'),
    // Seems declaring the type here is intended? mui-x maintainer did so in his example here:
    // https://github.com/mui/mui-x/issues/12914#issuecomment-2079571420
    valueGetter: (value: Company | null | undefined, row, column, apiRef) => value?.id,
  },
  {
    field: 'title',
    headerName: 'Title',
    type: 'string',
    display: "flex",
    width: 200,
  },
  {
    field: 'tags',
    headerName: 'Tags',
    type: 'string',
    editable: false,
    display: "flex",
    width: 200,
    valueGetter: (value, row, column, apiRef) => row.tags || [],
    valueFormatter: (value) => {
      // This is handled by renderCell, but keeping for completeness
      return '';
    },
  },
  {
    field: 'created_at',
    headerName: 'Created On',
    type: 'dateTime',
    width: 200,
    display: "flex",
    filterOperators: getGridDateOperators(false).filter(op => ['onOrAfter', 'onOrBefore'].includes(op.value)),
    valueGetter: (value, row, column, apiRef) => (
      DateTime.fromISO(value).toJSDate()
    ),
    valueFormatter: (value, row, column, apiRef) => (
      DateTime.fromJSDate(value).toLocaleString(DateTime.DATE_MED)
    ),
  },
];

const ContactTable = ({
  contacts, tags, onNavigateToContact, page, pageSize, rowCount, onPageChange, filter, onSetFilter, orgOptions,
  sort, onSetSort, loading, interactive, paginate, header, footer, defaultHiddenFields, hideTableButtons,
  onUpdateContact, sx, crmPreferencesData, onUpdateCrmPreferences, emptyMessage, showHiddenDateColumn
}: {
  contacts: ContactDenorm[];
  orgOptions: ContactOrgDenorm[];
  header?: ReactNode;
  footer?: ReactNode;
  tags: Tag[];
  page: number;
  pageSize: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onNavigateToContact: (id: ContactDenorm['id']) => void;
  filter?: GridFilterModel;
  onSetFilter: (filter: GridFilterModel | undefined) => void;
  sort?: GridSortModel;
  onSetSort: (filter: GridSortModel | undefined) => void;
  loading: boolean;
  interactive: boolean;
  paginate: boolean;
  defaultHiddenFields?: string[];
  hideTableButtons?: boolean;
  onUpdateContact: (
    contact: Partial<Omit<ContactDenorm, 'company'>> & { id: ContactDenorm['id'] }
  ) => Promise<ContactDenorm | undefined>
  sx?: SxProps;
  crmPreferencesData?: Array<{ 
    id: number; 
    contact_list_column_order: string[]; 
    contact_list_hidden_columns?: string[] 
  }>;
  onUpdateCrmPreferences?: (args: { 
    id: number; 
    patchedCrmPreferences: { contact_list_column_order?: string[]; contact_list_hidden_columns?: string[] } 
  }) => void;
  emptyMessage?: string | ReactNode;
  showHiddenDateColumn?: boolean;
}) => {
  const handleToggleHiddenDate = useCallback(async (contact: ContactDenorm) => {
    await onUpdateContact({
      id: contact.id,
      hidden_date: contact.hidden_date ? null : DateTime.now().toISO(),
    });
  }, [onUpdateContact]);

  const handleCellClick = useCallback<GridEventListener<'cellClick'>>((params, event, details) => {
    if (!['tags', 'hidden_date', 'Options'].includes(params.field)) {
      onNavigateToContact(params.row.id);
    }
  }, [onNavigateToContact]);

  const baseColumns = useMemo(() => {
    if (!showHiddenDateColumn) {
      return contactColumns;
    }

    return [
      ...contactColumns,
      {
        field: 'hidden_date',
        headerName: 'Hidden',
        type: 'boolean',
        sortable: false,
        filterable: false,
        display: "flex",
        width: 140,
        renderCell: ({ row }) => (
          <CabButton
            buttonType={row.hidden_date ? "secondary" : "tertiary"}
            onClick={(event) => {
              event.stopPropagation();
              handleToggleHiddenDate(row);
            }}
            sx={{ minWidth: 92 }}
          >
            {row.hidden_date ? 'Hidden' : 'Visible'}
          </CabButton>
        ),
      } satisfies CRMGridColDef<ContactDenorm>,
    ];
  }, [handleToggleHiddenDate, showHiddenDateColumn]);

  // Get column order from preferences or use default
  const orderedColumns = useMemo(() => {
    if (!crmPreferencesData?.[0]?.contact_list_column_order) {
      return baseColumns;
    }

    const savedOrder = crmPreferencesData[0].contact_list_column_order;
    const currentFieldNames = new Set(baseColumns.map(c => c.field));
    const savedFieldNames = new Set(savedOrder);

    // Filter out any saved fields that no longer exist and blank columns
    const validSavedFields = savedOrder.filter(field => currentFieldNames.has(field) && field !== '');
    
    // Add any new fields that weren't in the saved order (excluding blank columns)
    const newFields = baseColumns
      .filter(col => !savedFieldNames.has(col.field) && col.field !== '')
      .map(col => col.field);
    
    const finalOrder = [...validSavedFields, ...newFields];
    
    // Reorder columns based on the final order
    return finalOrder
      .map(fieldName => baseColumns.find(col => col.field === fieldName))
      .filter(Boolean) as typeof baseColumns;
  }, [baseColumns, crmPreferencesData]);

  // Create lookup map for tags
  const tagsMap = useMemo(() => Object.fromEntries(tags.map(tag => [tag.id, tag])), [tags]);

  // Create options and rawOptions for tags filter
  const tagOptions = useMemo(() => tags.map(tag => ({
    value: tag.id,
    label: tag.name
  })), [tags]);

  const tagsRawOptions = useMemo(() => Object.fromEntries(
    tags.map(tag => [String(tag.id), tag])
  ) as Record<string, AbstractEventAnalyticsLabel>, [tags]);

  const columns = useMemo(() => orderedColumns.map(c => {
    if (c.field === 'tags') {
      return {
        ...c,
        filterOperators: selectFilterOperator([
          {
            options: tagOptions,
            rawOptions: tagsRawOptions
          }
        ]),
        renderCell: (params: GridRenderCellParams<ContactDenorm>) => {
          const tagIds = (params.row.tags || []) as number[];
          const selectedTags = tagIds
            .map((tagId: number) => tagsMap[tagId])
            .filter((tag): tag is Tag => !!tag);
        
          if (selectedTags.length === 0) {
            return (
              <CabToggleChip 
                chipColor={colors.white800} 
                label="None" 
                selected={true} 
              />
            );
          }
        
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5
              }}>
              {selectedTags.map(tag => (
                <CabToggleChip 
                  key={tag.id}
                  chipColor={tag.color || colors.white800} 
                  label={tag.name} 
                  selected={true} 
                />
              ))}
            </Box>
          );
        },
      };
    } else if (c.field === 'company') {
      return {
        ...c,
        valueOptions: ({ row }: GridValueOptionsParams<ContactDenorm>) => {
          if (!row?.company) return [];
          return [{ value: row.company.id, label: row.company.name || '' }];
        },
      };
    }
    return c;
  }), [orderedColumns, tagsMap, tagOptions, tagsRawOptions]);

  // Create column visibility model from saved preferences
  const columnVisibilityModel = useMemo(() => {
    if (!crmPreferencesData?.[0]?.contact_list_hidden_columns) {
      return {};
    }
    
    const hiddenColumns = crmPreferencesData[0].contact_list_hidden_columns;
    const model: GridColumnVisibilityModel = {};
    
    // Set hidden columns to false
    hiddenColumns.forEach(field => {
      if (field !== '') { // Filter out blank column
        model[field] = false;
      }
    });
    
    return model;
  }, [crmPreferencesData]);

  const handleContactRowUpdate = useCallback(async (updatedRow: ContactDenorm, originalRow: ContactDenorm) => {
    const update: ContactDenorm = {
      ...updatedRow,
      tags: updatedRow.tags || [],
    };
    const res = await onUpdateContact(update);
    return res || originalRow;
  }, [onUpdateContact]);

  const handleColumnOrderChange = useCallback<GridEventListener<'columnOrderChange'>>((params, event, details) => {
    if (crmPreferencesData?.[0] && onUpdateCrmPreferences && details.api) {
      const allColumns = details.api.getAllColumns();
      const newOrder = allColumns
        .filter(col => col.field !== '') // Filter out the blank column used for rendering
        .map(col => col.field);
      onUpdateCrmPreferences({
        id: crmPreferencesData[0].id,
        patchedCrmPreferences: {
          contact_list_column_order: newOrder
        }
      });
    }
  }, [crmPreferencesData, onUpdateCrmPreferences]);

  const handleColumnVisibilityChange = useCallback((model: GridColumnVisibilityModel) => {
    if (crmPreferencesData?.[0] && onUpdateCrmPreferences) {
      const hiddenColumns = Object.entries(model)
        .filter(([field, isVisible]) => !isVisible && field !== '') // Filter out blank column
        .map(([field]) => field);
      
      onUpdateCrmPreferences({
        id: crmPreferencesData[0].id,
        patchedCrmPreferences: {
          contact_list_hidden_columns: hiddenColumns
        }
      });
    }
  }, [crmPreferencesData, onUpdateCrmPreferences]);

  return (
    <>
      {header}
      <Box sx={sx}>
        <CRMTable
          columns={columns}
          rows={contacts}
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPageChange={onPageChange}
          onCellClick={handleCellClick}
          filter={filter}
          onSetFilter={onSetFilter}
          sort={sort}
          onSetSort={onSetSort}
          loading={loading}
          interactive={interactive}
          paginate={paginate}
          defaultHiddenFields={defaultHiddenFields}
          hideTableButtons={hideTableButtons}
          onRowUpdate={handleContactRowUpdate}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnVisibilityModel={columnVisibilityModel}
          emptyMessage={emptyMessage}
          // actions={[{ name: 'Hide', key: 'hide', onClick: (row) => handleHideContact(row.id), icon: IoEyeOff }]}
        />
      </Box>
      {footer}
    </>
  );
};

export default ContactTable;
